import keyBy from "lodash/keyBy";
import difference from "lodash/difference";
import omit from "lodash/omit";
import findKey from "lodash/findKey";
import values from "lodash/values";
import cloneDeep from "lodash/cloneDeep";
import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import moment from "moment-timezone";
import { meetingAPI, API_V1, API_V2 } from "@/api";
import { i18n } from "@/i18n";
import pathPush from "@/lib/pathPush";
import OutcomeType from "@/constants/OutcomeType";
import TextTriggers from "@/constants/TextTriggers";
import debugWrapper from "@/helpers/debugWrapper";
import { createNewOutcome, create as createOutcomeHelper, mapOutcomeBody } from "@/store/modules/outcomeHelpers";
import { create as createTopicHelper } from "@/store/modules/topicHelpers";

import {
	TopicOutcomeCreatedEvent,
	TopicOutcomeEditedEvent,
	TopicOutcomeDeletedEvent,
	TopicOutcomeCompletedEvent
} from "@/telemetry";
import MeetingPermissions from "@/permissions/MeetingPermissions";

const findAbsentOutcomeIds = ( outcomes, outcomeFieldMap ) => {
	const freshOutcomeIds = outcomes.map( outcome => outcome.id );
	const staleOutcomeIds = Object.keys( outcomeFieldMap );
	return difference( staleOutcomeIds, freshOutcomeIds );
};

const findAbsentTopicIds = ( topics, topicFieldMap ) => {
	const freshTopicIds = topics.map( topic => topic.id );
	const staleTopicIds = Object.keys( topicFieldMap );
	return difference( staleTopicIds, freshTopicIds );
};

const actions = debugWrapper( "teaming:store:meetingAgenda:actions", {
	initializeAgenda: async ( { commit, rootGetters, /* state, rootState, */ }, { meeting, } ) => {
		commit( "setFlag", { flag: "hasInitializedMeetingAgendaStore", is: false, } );
		commit( "setFlag", { flag: "isInitializingMeetingAgendaStore", is: true, } );

		const userProfile = rootGetters[ "userProfile/userProfile" ];
		const timezone = rootGetters[ "auth/timezone" ];
		const { id: meetingId, } = meeting;
		const meetingPermissions = ( new MeetingPermissions( {
			userProfile,
			timezone,
			meeting,
		} ) ).resolve();

		meeting.permissions = meetingPermissions;

		const inviteeMap = keyBy( meeting.invitees, "personId" );

		const meetingOutcomes = await meetingAPI.fetchOutcomes( { meetingId, } );

		const meetingOutcomeMap = {};
		const topicOutcomeIdMap = {};
		const topicFieldMap = {};
		const newOutcomeFieldMap = {};
		const outcomeFieldMap = {};

		const topicHelper = createTopicHelper( { meeting, userProfile, timezone, } );
		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		meetingOutcomes.forEach( meetingOutcome => {
			const { id, topicId, } = meetingOutcome;

			const outcomeField = outcomeHelper.createField( meetingOutcome );

			outcomeFieldMap[ id ] = outcomeField;
			meetingOutcomeMap[ id ] = cloneDeep( outcomeField.currentOutcome );

			pathPush( topicOutcomeIdMap, topicId, id );
		} );

		meeting.topics.forEach( topic => {
			const { id: topicId, } = topic;

			// create a new empty outcome for topics that don't have one yet
			if ( !( topicId in newOutcomeFieldMap ) ) {
				const newOutcome = outcomeHelper.createNewOutcome( OutcomeType.NOTE );
				newOutcomeFieldMap[ topicId ] = outcomeHelper.createField( newOutcome );
			}

			topicFieldMap[ topicId ] = topicHelper.createField( topic );
		} );

		const assignedTeam = {
			...meeting.teams[ 0 ] || {
				id: "",
				type: "",
			},
		};

		const organizer = {
			...meeting.organizer,
		};

		const invitees = [
			...meeting.invitees,
		];

		commit( "initialized", {
			assignedTeam,
			invitees,
			meeting,
			meetingInviteeMap: inviteeMap,
			meetingOutcomeMap,
			meetingPermissions,
			newOutcomeFieldMap,
			organizer,
			outcomeFieldMap,
			timezone,
			topicFieldMap,
			topicOutcomeIdMap,
			userProfile,
		} );

		commit( "setFlag", { flag: "isInitializingMeetingAgendaStore", is: false, } );
		commit( "setFlag", { flag: "hasInitializedMeetingAgendaStore", is: true, } );
	},
	updateAgenda: async ( { commit, state, rootGetters, /* rootState, */ }, { meeting, } ) => {
		// only update for the active meeting ID
		if ( state.agendaMeetingId !== meeting.id ) {
			return;
		}

		const { id: meetingId, } = meeting;

		const userProfile = rootGetters[ "userProfile/userProfile" ];
		const timezone = rootGetters[ "auth/timezone" ];

		const meetingPermissions = ( new MeetingPermissions( {
			userProfile,
			timezone,
			meeting,
		} ) ).resolve();

		meeting = {
			...meeting,
			permissions: meetingPermissions,
		};

		const fetchedOutcomes = await meetingAPI.fetchOutcomes( { meetingId, } );

		const topicOutcomeIdMap = {};
		const newerOutcomeFieldMap = {};

		//
		// Set up topic and outcome helpers
		//

		const topicHelper = createTopicHelper( { meeting, userProfile, timezone, } );
		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		//
		// Remove absent topics/outcomes from existing maps.
		// TODO: but should probably still display them until the page refreshes
		//

		const absentTopicIds = findAbsentTopicIds( meeting.topics, state.topicFieldMap );
		const prunedTopicFieldMap = omit( state.topicFieldMap, absentTopicIds );

		const absentOutcomeIds = findAbsentOutcomeIds( fetchedOutcomes, state.outcomeFieldMap );
		const prunedOutcomeFieldMap = omit( state.outcomeFieldMap, absentOutcomeIds );
		const prunedMeetingOutcomeMap = omit( state.meetingOutcomeMap, absentOutcomeIds );

		fetchedOutcomes.forEach( outcome => {
			const { id: outcomeId, topicId, } = outcome;
			const unrecognizedOutcome = !( outcomeId in prunedOutcomeFieldMap );

			if ( unrecognizedOutcome ) {
				const newOutcomeField = outcomeHelper.createField( outcome );
				prunedMeetingOutcomeMap[ outcomeId ] = cloneDeep( newOutcomeField.currentOutcome );
				prunedOutcomeFieldMap[ outcomeId ] = newOutcomeField;
			} else {
				const { isDirty, currentOutcome, } = prunedOutcomeFieldMap[ outcomeId ];
				// if it's not dirty just override it, setting both the pristine and
				// current outcomes together; if it IS dirty, we want to preserve the
				// state of the current outcome, because it has been edited
				const updatedOutcomeField = isDirty ?
					outcomeHelper.createField( outcome, currentOutcome ) :
					outcomeHelper.createField( outcome );
				prunedMeetingOutcomeMap[ outcomeId ] = cloneDeep( updatedOutcomeField.currentOutcome );
				prunedOutcomeFieldMap[ outcomeId ] = updatedOutcomeField;
			}

			pathPush( topicOutcomeIdMap, topicId, outcomeId );
		} );

		meeting.topics.forEach( topic => {
			const { id: topicId, } = topic;
			const unrecognizedTopic = !( topicId in prunedTopicFieldMap );

			if ( unrecognizedTopic ) {
				const newOutcome = outcomeHelper.createNewOutcome( OutcomeType.NOTE );
				const newOutcomeField = outcomeHelper.createField( newOutcome );
				newerOutcomeFieldMap[ topicId ] = newOutcomeField;

				const newTopicField = topicHelper.createField( topic );
				prunedTopicFieldMap[ topicId ] = newTopicField;
			} else {
				const { isDirty, currentTopic, } = prunedTopicFieldMap[ topicId ];
				const updatedTopicField = isDirty ?
					topicHelper.createField( topic, currentTopic ) :
					topicHelper.createField( topic );
				prunedTopicFieldMap[ topicId ] = updatedTopicField;
			}
		} );

		const assignedTeam = {
			...meeting.teams[ 0 ] || {
				id: "",
				type: "",
			},
		};

		const organizer = {
			...meeting.organizer,
		};

		const invitees = [
			...meeting.invitees,
		];

		commit( "updated", {
			assignedTeam,
			invitees,
			meetingOutcomeMap: prunedMeetingOutcomeMap,
			meetingPermissions,
			newOutcomeFieldMap: {
				...state.newOutcomeFieldMap,
				...newerOutcomeFieldMap,
			},
			organizer,
			outcomeFieldMap: prunedOutcomeFieldMap,
			topicFieldMap: prunedTopicFieldMap,
			topicOutcomeIdMap,
		} );
	},
	resetAgenda: async ( { commit, /* state, rootState, rootGetters, */ } ) => {
		commit( "initialized", {
			meeting: {},
			meetingInviteeMap: {},
			meetingOutcomeMap: {},
			newOutcomeFieldMap: {},
			outcomeFieldMap: {},
			timezone: "",
			topicFieldMap: {},
			topicOutcomeIdMap: {},
			userProfile: {},
		} );

		commit( "setFlag", { flag: "hasInitializedMeetingAgendaStore", is: false, } );
	},

	//
	// existing outcomes
	//

	storeExistingOutcome: ( { state, commit, }, { currentOutcome, } ) => {
		const { meeting, userProfile, timezone, } = state;
		const pristineOutcome = state.meetingOutcomeMap[ currentOutcome.id ];
		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );
		const updatedOutcomeField = outcomeHelper.createField( { ...pristineOutcome, }, currentOutcome );
		commit( "existingOutcomeFieldUpdated", { outcomeField: updatedOutcomeField, } );
	},
	saveExistingOutcome: async ( { commit, state, rootGetters, /* rootState, */ }, { outcome, cancelEdit = true, } ) => {
		commit( "setFlag", { flag: "isSavingTopicOutcome", is: true, } );
		commit( "setFlag", { flag: "isSavingExistingTopicOutcome", is: true, } );
		Nprogress.start();

		let saveSuccessful = true;

		try {
			const meetingId = state.agendaMeetingId;
			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];
			const mappedOutcomeBody = mapOutcomeBody( outcome, "utc", API_V2 );

			const updatedOutcome = await meetingAPI.updateOutcome( {
				meetingId,
				outcome: mappedOutcomeBody,
			} );

			commit( "schedule/outcomeUpdated", {
				meetingId,
				updatedOutcome,
				userProfile,
				timezone,
			}, { root: true, } );

			commit( "outcomeSaved", { outcome: updatedOutcome, } );

			if ( cancelEdit ) {
				commit( "editOutcome", { outcomeId: null, } );
			}

			const telemetryEvent = new TopicOutcomeEditedEvent( meetingId, updatedOutcome.type );
			telemetryEvent.submit();
		} catch ( e ) {
			saveSuccessful = false;

			const message = i18n.t( "meetingAgendaStore.saveExistingOutcome_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}

		commit( "setFlag", { flag: "isSavingTopicOutcome", is: false, } );
		commit( "setFlag", { flag: "isSavingExistingTopicOutcome", is: false, } );
		Nprogress.done();

		// Needed for telemetry.
		// @see changeExistingOutcomeStatus
		return saveSuccessful;
	},
	saveDirtyExistingOutcomes: async ( { commit, state, rootGetters, /* getters, rootState */ }, { suppressProgress, } = { suppressProgress: false, } ) => {
		commit( "setFlag", { flag: "isSavingTopicOutcome", is: true, } );
		commit( "setFlag", { flag: "isSavingExistingTopicOutcome", is: true, } );
		if ( !suppressProgress ) {
			Nprogress.start();
		}

		try {
			const { editingOutcomeId: previousEditingOutcomeId, } = state;
			const meetingId = state.agendaMeetingId;
			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];

			const updatePromises = values( state.outcomeFieldMap )
				.filter( outcomeField => {
					return outcomeField.isDirty;
				} )
				.map( dirtyOutcomeField => {
					const { currentOutcome: outcome, } = dirtyOutcomeField;

					const mappedOutcomeBody = mapOutcomeBody( outcome, "utc", API_V2 );

					const response = meetingAPI.updateOutcome( {
						meetingId,
						topicId: outcome.topicId,
						outcome: mappedOutcomeBody,
					} );

					return response.then( updatedOutcome => {
						const telemetryEvent = new TopicOutcomeEditedEvent( meetingId, updatedOutcome.type );
						telemetryEvent.submit();

						return updatedOutcome;
					} );
				} );

			const updatedOutcomes = await Promise.all( updatePromises );

			updatedOutcomes.forEach( updatedOutcome => {
				commit( "schedule/outcomeUpdated", {
					meetingId,
					updatedOutcome,
					userProfile,
					timezone,
				}, { root: true, } );

				commit( "outcomeSaved", { outcome: updatedOutcome, } );
			} );

			if ( previousEditingOutcomeId === state.editingOutcomeId ) {
				commit( "editOutcome", { outcomeId: null, } );
			}
		} catch ( e ) {
			const message = i18n.t( "meetingAgendaStore.saveExistingOutcome_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}

		commit( "setFlag", { flag: "isSavingTopicOutcome", is: false, } );
		commit( "setFlag", { flag: "isSavingExistingTopicOutcome", is: false, } );
		Nprogress.done();
	},
	resetExistingOutcome: ( { state, commit, }, { currentOutcome, cancelEdit = true, } ) => {
		const { meeting, userProfile, timezone, } = state;
		const outcomeField = state.outcomeFieldMap[ currentOutcome.id ];
		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		if ( outcomeField.isDirty ) {
			const pristineOutcome = state.meetingOutcomeMap[ currentOutcome.id ];
			const pristineField = outcomeHelper.createField( { ...pristineOutcome, } );
			commit( "existingOutcomeFieldUpdated", { outcomeField: pristineField, } );
		}

		if ( cancelEdit ) {
			commit( "editOutcome", { outcomeId: null, } );
		}
	},
	resetExistingOutcomes: async ( { commit, state, /* rootState, rootGetters, */ }, { cancelEdit, } = { cancelEdit: true, } ) => {
		const { meeting, userProfile, timezone, } = state;
		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		const dirtyOutcomeFields = values( state.outcomeFieldMap )
			.filter( outcomeField => outcomeField.isDirty );

		dirtyOutcomeFields.forEach( dirtyOutcomeField => {
			const pristineOutcome = state.meetingOutcomeMap[ dirtyOutcomeField.id ];
			const pristineField = outcomeHelper.createField( pristineOutcome );
			commit( "existingOutcomeFieldUpdated", { outcomeField: pristineField, } );
		} );

		if ( cancelEdit ) {
			commit( "editOutcome", { outcomeId: null, } );
		}
	},
	changeExistingOutcomeStatus: async ( { dispatch, state, /* rootState, rootGetters, */ }, { currentOutcome, isCompleted, } ) => {
		const { timezone, } = state;

		const updatedOutcome = {
			...currentOutcome,
			completed: isCompleted ?
				moment.tz( new Date(), timezone ).unix() :
				null,
		};

		const saveSuccessful = await dispatch( "saveExistingOutcome", { outcome: updatedOutcome, } );

		if ( saveSuccessful ) {
			const meetingId = state.agendaMeetingId;
			const telemetryEvent = new TopicOutcomeCompletedEvent( meetingId );
			telemetryEvent.submit();
		}
	},
	editExistingOutcome: async ( { commit, dispatch, /* state, rootState, rootGetters, */ }, { outcomeId, } ) => {
		await dispatch( "saveDirtyExistingOutcomes", { suppressProgress: true, } );
		commit( "editOutcome", { outcomeId, } );
	},
	deleteExistingOutcome: async ( { commit, state, /* rootState, rootGetters */ }, { topicId, outcomeId, } ) => {
		commit( "setFlag", { flag: "isDeletingExistingTopicOutcome", is: true, } );
		Nprogress.start();

		try {
			const { agendaMeetingId: meetingId, } = state;
			const outcome = state.meetingOutcomeMap[ outcomeId ];
			await meetingAPI.deleteOutcome( { meetingId, outcomeId, } );
			commit( "outcomeDeleted", { topicId, outcomeId, } );

			const telemetryEvent = new TopicOutcomeDeletedEvent( meetingId, outcome.type );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "meetingAgendaStore.deleteExistingOutcome_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}

		commit( "setFlag", { flag: "isDeletingExistingTopicOutcome", is: false, } );
		Nprogress.done();
	},

	//
	// new outcome
	//

	completeNewOutcomeCommand: async ( { commit, state, /* rootState, rootGetters, */ }, { topicId, } ) => {
		const {
			pristineOutcome,
			currentOutcome,
			contentWithCommand,
			hasPartialCommand,
		} = state.newOutcomeFieldMap[ topicId ];

		if ( !hasPartialCommand ) {
			return;
		}

		const { meeting, userProfile, timezone, } = state;
		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		const newOutcome = {
			...currentOutcome,
			content: contentWithCommand,
		};

		const newOutcomeField = outcomeHelper.createField( pristineOutcome, newOutcome );
		commit( "newOutcomeFieldUpdated", { topicId, newOutcomeField, } );
	},
	changeNewOutcomeCommand: async ( { commit, state, /* rootState, rootGetters, */ }, { topicId, outcomeType, } ) => {
		const textTrigger = TextTriggers[ outcomeType ];
		const { meeting, userProfile, timezone, } = state;
		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );
		const { pristineOutcome, currentOutcome, contentSansCommand, } = state.newOutcomeFieldMap[ topicId ];

		const newOutcome = {
			...currentOutcome,
			content: `${contentSansCommand.trim()} ${textTrigger}`,
		};

		const newOutcomeField = outcomeHelper.createField( pristineOutcome, newOutcome );
		commit( "newOutcomeFieldUpdated", { topicId, newOutcomeField, } );
	},
	executeNewOutcomeCommand: async ( { commit, state, /* rootState, rootGetters, */ }, { topicId, } ) => {
		const {
			pristineOutcome,
			currentOutcome,
			commandTrigger,
			contentSansCommand,
			hasFullCommand,
		} = state.newOutcomeFieldMap[ topicId ];

		if ( !hasFullCommand ) {
			return;
		}

		const newOutcomeType = findKey( TextTriggers, trigger => trigger.startsWith( commandTrigger ) );
		const newOutcome = {
			...currentOutcome,
			type: newOutcomeType,
			content: contentSansCommand,
		};

		const { meeting, userProfile, timezone, } = state;
		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		const newOutcomeField = outcomeHelper.createField( pristineOutcome, newOutcome );
		commit( "newOutcomeFieldUpdated", { topicId, newOutcomeField, } );
	},
	changeNewOutcomeType: ( { commit, state, }, { topicId, newOutcomeType, } ) => {
		const {
			pristineOutcome,
			currentOutcome,
			contentSansCommand,
		} = state.newOutcomeFieldMap[ topicId ];

		const newOutcome = {
			...currentOutcome,
			content: contentSansCommand,
			type: newOutcomeType,
		};

		const { meeting, userProfile, timezone, } = state;
		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		const newOutcomeField = outcomeHelper.createField( pristineOutcome, newOutcome );
		commit( "newOutcomeFieldUpdated", { topicId, newOutcomeField, } );
	},
	resetNewOutcome: ( { commit, state, }, { topicId, } ) => {
		const newOutcome = createNewOutcome( OutcomeType.NOTE );
		const { meeting, userProfile, timezone, } = state;
		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );
		const newOutcomeField = outcomeHelper.createField( newOutcome );
		commit( "newOutcomeFieldUpdated", { topicId, newOutcomeField, } );
	},
	storeNewOutcome: async ( { commit, state, /* rootState, rootGetters, */ }, { topicId, currentOutcome, } ) => {
		const { pristineOutcome, } = state.newOutcomeFieldMap[ topicId ];
		const { meeting, userProfile, timezone, } = state;
		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );
		const newOutcomeField = outcomeHelper.createField( pristineOutcome, currentOutcome );
		commit( "newOutcomeFieldUpdated", { topicId, newOutcomeField, } );
	},
	saveNewOutcome: async ( { commit, state, dispatch, rootGetters, /* rootState, */ }, { topicId, } ) => {
		commit( "setFlag", { flag: "isSavingTopicOutcome", is: true, } );
		commit( "setFlag", { flag: "isSavingNewTopicOutcome", is: true, } );
		Nprogress.start();

		try {
			const meetingId = state.agendaMeetingId;
			const timezone = rootGetters[ "auth/timezone" ];
			const userProfile = rootGetters[ "userProfile/userProfile" ];

			const outcome = state.newOutcomeFieldMap[ topicId ].currentOutcome;
			const mappedOutcomeBody = mapOutcomeBody( outcome, "utc", API_V1 );

			const createdOutcome = await meetingAPI.createOutcome( {
				newOutcome: { ...mappedOutcomeBody, meetingId, topicId, },
			} );

			commit( "schedule/outcomeCreated", {
				meetingId,
				createdOutcome,
				userProfile,
				timezone,
			}, { root: true, } );

			commit( "outcomeSaved", { outcome: createdOutcome, } );

			commit( "editOutcome", { outcomeId: null, } );

			await dispatch( "resetNewOutcome", { topicId, } );

			const telemetryEvent = new TopicOutcomeCreatedEvent( meetingId, createdOutcome.type );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "meetingStore.saveNewOutcome_errorMessage", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}

		commit( "setFlag", { flag: "isSavingTopicOutcome", is: false, } );
		commit( "setFlag", { flag: "isSavingNewTopicOutcome", is: false, } );
		Nprogress.done();
	},
}, { skipFirstArg: true, } );

export default actions;



// WEBPACK FOOTER //
// ./src/store/modules/meetingAgenda/actions.js